"use client";

import { useUserTeamForTournament } from "@/hooks/useUserTeamForTournament";
import type { UserTeamWithTournament } from "@/hooks/useUserTeams";
import { Link } from "@/i18n/routing";
import type { Chat, ChatInboxEntry, Tournament } from "@/types";
import type { ChatInboxGroups } from "@/hooks/useChatInbox";
import { Icon } from "@iconify/react";
import { formatDistanceToNow } from "date-fns";
import { enUS, fi } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";
import { useMemo } from "react";

interface UnifiedChatDropdownProps {
  groups: ChatInboxGroups;
  /** Tournaments the current user participates in. Used to surface tournament
   *  chats that have no inbox entry yet (fresh participants who haven't
   *  exchanged messages). */
  userTournaments: Tournament[];
  /** Teams the current user is on, with tournament context. Used to surface
   *  team chats with no inbox entry yet. */
  userTeams: UserTeamWithTournament[];
  /** Active match chats the user participates in. Used to surface match
   *  chats that exist (created at challenge time) but have no inbox entry
   *  yet because no one has posted. */
  userMatchChats: Chat[];
  isLoading: boolean;
  onClose: () => void;
  currentUserId: string | undefined;
}

/**
 * Grouped chat list — Tournament / Team / Match sections in that order.
 * Empty sections are hidden so the dropdown stays compact when a user
 * doesn't have, say, an active match chat.
 */
export function UnifiedChatDropdown({
  groups,
  userTournaments,
  userTeams,
  userMatchChats,
  isLoading,
  onClose,
  currentUserId,
}: UnifiedChatDropdownProps) {
  const t = useTranslations();

  // Merge inbox entries with placeholders for accessible-but-quiet chats.
  // Inbox-backed entries (with previews / unread / timestamps) win — fallbacks
  // only appear when the user has access but the chat has no inbox row yet.
  const mergedGroups = useMemo<ChatInboxGroups>(() => {
    const tournamentIds = new Set(groups.tournament.map((e) => e.tournamentId));
    const teamIds = new Set(groups.team.map((e) => e.chatId));
    const matchChatIds = new Set(groups.match.map((e) => e.chatId));

    const tournamentPlaceholders: ChatInboxEntry[] = userTournaments
      .filter((t) => !tournamentIds.has(t.id))
      .map((t) => makePlaceholder("tournament", t.id, t.id, t.slug, t.name));

    const teamPlaceholders: ChatInboxEntry[] = userTeams
      .filter((team) => !teamIds.has(team.teamId))
      .map((team) =>
        makePlaceholder(
          "team",
          team.teamId,
          team.tournamentId,
          team.tournamentSlug,
          team.teamName ?? team.tournamentName,
        ),
      );

    const matchPlaceholders: ChatInboxEntry[] = userMatchChats
      .filter((chat) => !matchChatIds.has(chat.id))
      .map((chat) => ({
        ...makePlaceholder(
          "match",
          chat.id,
          chat.tournamentId,
          chat.tournamentSlug,
          chat.team1Name || chat.team2Name || "",
        ),
        team1Id: chat.team1Id,
        team1Name: chat.team1Name,
        team2Id: chat.team2Id,
        team2Name: chat.team2Name,
      }));

    return {
      tournament: [...groups.tournament, ...tournamentPlaceholders],
      team: [...groups.team, ...teamPlaceholders],
      match: [...groups.match, ...matchPlaceholders],
    };
  }, [groups, userTournaments, userTeams, userMatchChats]);

  const totalCount =
    mergedGroups.tournament.length +
    mergedGroups.team.length +
    mergedGroups.match.length;

  return (
    <div className="absolute right-0 left-1 lg:left-auto mt-3 w-[100vw-1px] lg:w-[400px] lg:max-w-[95vw] bg-[var(--bg-card)] rounded-xl shadow-2xl z-50 max-h-[500px] overflow-hidden border border-[var(--border-gold)] backdrop-blur-sm">
      <div className="px-4 py-2.5 border-b border-[var(--border-subtle)] bg-[var(--bg-card-hover)]/40">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--gold)]">
          {t("chat.bell.title")}
        </h2>
      </div>
      <div className="overflow-y-auto max-h-[calc(500px-41px)] custom-scrollbar">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--gold)]" />
          </div>
        ) : totalCount === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Icon
              icon="solar:chat-round-dots-bold"
              className="text-4xl mb-3 opacity-50 text-[var(--gold)]"
              aria-hidden="true"
            />
            <p className="text-muted text-sm">{t("chat.bell.empty")}</p>
            <p className="text-muted text-xs mt-1">
              {t("chat.bell.emptyHint")}
            </p>
            <Link
              href="/tournaments"
              onClick={onClose}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-[var(--border-gold)] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--gold)] transition-colors hover:bg-[var(--gold)]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)]/50"
            >
              {t("chat.bell.emptyAction")}
              <Icon
                icon="solar:arrow-right-linear"
                className="text-sm"
                aria-hidden="true"
              />
            </Link>
          </div>
        ) : (
          <>
            <Section
              titleKey="chat.bell.section.tournament"
              entries={mergedGroups.tournament}
              currentUserId={currentUserId}
              onNavigate={onClose}
              hrefFor={(e) => `/tournaments/${e.tournamentSlug}/chat`}
              fallbackName={t("chat.tournamentChat")}
              icon="solar:chat-round-dots-bold"
            />
            <Section
              titleKey="chat.bell.section.team"
              entries={mergedGroups.team}
              currentUserId={currentUserId}
              onNavigate={onClose}
              hrefFor={(e) => `/tournaments/${e.tournamentSlug}/chat/team`}
              fallbackName={t("chat.teamChat")}
              icon="solar:users-group-rounded-linear"
            />
            <Section
              titleKey="chat.bell.section.match"
              entries={mergedGroups.match}
              currentUserId={currentUserId}
              onNavigate={onClose}
              hrefFor={(e) =>
                `/tournaments/${e.tournamentSlug}/chat/match/${e.chatId}`
              }
              fallbackName={t("chat.matchChat")}
              icon="game-icons:crossed-swords"
              isMatch
            />
          </>
        )}
      </div>
    </div>
  );
}

interface SectionProps {
  titleKey: string;
  entries: ChatInboxEntry[];
  currentUserId: string | undefined;
  onNavigate: () => void;
  hrefFor: (entry: ChatInboxEntry) => string;
  fallbackName: string;
  icon: string;
  isMatch?: boolean;
}

function Section({
  titleKey,
  entries,
  currentUserId,
  onNavigate,
  hrefFor,
  fallbackName,
  icon,
  isMatch,
}: SectionProps) {
  const t = useTranslations();
  if (entries.length === 0) return null;
  return (
    <div>
      <h3 className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted bg-[var(--bg-card-hover)]/30">
        {t(titleKey)}
      </h3>
      <ul className="divide-y divide-[var(--border-subtle)]">
        {entries.map((entry) => (
          <ChatRow
            key={entry.id}
            entry={entry}
            currentUserId={currentUserId}
            onNavigate={onNavigate}
            href={hrefFor(entry)}
            fallbackName={fallbackName}
            icon={icon}
            isMatch={!!isMatch}
          />
        ))}
      </ul>
    </div>
  );
}

interface ChatRowProps {
  entry: ChatInboxEntry;
  currentUserId: string | undefined;
  onNavigate: () => void;
  href: string;
  fallbackName: string;
  icon: string;
  isMatch: boolean;
}

function ChatRow({
  entry,
  currentUserId,
  onNavigate,
  href,
  fallbackName,
  icon,
  isMatch,
}: ChatRowProps) {
  const t = useTranslations();
  const locale = useLocale();
  const dateLocale = locale === "fi" ? fi : enUS;

  // For match chats the inbox stores both team names + ids — pick the
  // opponent based on the user's current team in this tournament. For
  // tournament/team rows, the displayName is denormalized at fan-out time.
  const { data: userTeam } = useUserTeamForTournament(
    isMatch ? entry.tournamentId : undefined,
  );

  let label: string;
  if (isMatch) {
    label =
      userTeam?.id === entry.team1Id
        ? entry.team2Name ?? fallbackName
        : (entry.team1Name ?? fallbackName);
  } else {
    label = entry.displayName ?? fallbackName;
  }

  const lastMessageAt = entry.lastMessageAt
    ? new Date(entry.lastMessageAt)
    : null;
  const timeAgo = lastMessageAt
    ? formatDistanceToNow(lastMessageAt, {
        addSuffix: true,
        locale: dateLocale,
      })
    : null;
  const isOwn = entry.lastMessageSenderId === currentUserId;
  const preview = entry.lastMessagePreview;
  const unread = entry.unreadCount ?? 0;

  return (
    <li>
      <Link
        href={href}
        onClick={onNavigate}
        className="w-full p-4 text-left hover:bg-[var(--bg-card-hover)] transition-colors flex items-start gap-3"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[var(--gold-dark)] to-[var(--gold)] flex items-center justify-center">
          <Icon
            icon={icon}
            className="text-base text-[var(--gold-light)]"
            aria-hidden="true"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-[var(--text-primary)] truncate">
              {label}
            </span>
            {timeAgo && (
              <span className="text-xs text-muted flex-shrink-0">
                {timeAgo}
              </span>
            )}
          </div>

          <p className="text-sm text-muted truncate mt-0.5">
            {preview ? (
              <>
                {isOwn && (
                  <span className="text-[var(--gold)]">{t("chat.you")}: </span>
                )}
                {preview}
              </>
            ) : (
              <span className="italic">{t("chat.noMessages")}</span>
            )}
          </p>
        </div>

        {unread > 0 && (
          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-xs font-bold text-white bg-gradient-to-br from-red-500 to-red-600 rounded-full">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Link>
    </li>
  );
}

/**
 * Build a synthetic inbox entry for chats the user can access but hasn't
 * received messages in yet. ChatRow renders these gracefully — no timestamp,
 * no preview (falls back to "No messages yet"), and no unread badge — while
 * still routing to the right surface via the section's `hrefFor`.
 */
function makePlaceholder(
  type: ChatInboxEntry["type"],
  chatId: string,
  tournamentId: string,
  tournamentSlug: string,
  displayName: string,
): ChatInboxEntry {
  const now = new Date();
  return {
    id: chatId,
    chatId,
    type,
    tournamentId,
    tournamentSlug,
    displayName,
    unreadCount: 0,
    archived: false,
    createdAt: now,
    updatedAt: now,
  };
}
